/**
 * Shared MissionBreadcrumb — Sprint 1070.A2-cmd-followup.
 *
 * Single canonical mission-breadcrumb pill row, consumed by:
 *   - IndustryStudioPage (Finance / Operate) — passes `renderDrawer` so the
 *     count badge opens an "oldest at this stage" stale-rows drawer.
 *   - CreativeStudioPage (Sprint 1070.A2-cmd-followup) — omits `renderDrawer`;
 *     the count badge becomes a `<Link>` deep-linking to the stage's href.
 *
 * Visual = the same rounded pills + numeric badge circles + chevron dividers
 * + accent-tinted "MISSION:" label paco approved across all studios.
 *
 * Props
 *   missionLabel : string                — already-localized mission tagline
 *   sections     : Array<{
 *                    slug : string,
 *                    label: string,        — already-localized stage label
 *                    count: number | null,
 *                    href ?: string         — deep-link target (used by Creative)
 *                  }>
 *   accent       : string                — hex (with or without #), studio accent
 *   studioId     : string                — used for data-testid prefixes
 *   t            : i18n t() function
 *   renderDrawer ?: (ctx) => JSX | null  — optional. When provided, count badge
 *                  is a button that toggles the drawer. ctx = { slug, count,
 *                  anchorRef, onClose }. When omitted, count badge is a Link
 *                  using sections[i].href.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export default function MissionBreadcrumb({
  missionLabel,
  sections,
  accent,
  studioId,
  t,
  renderDrawer,
  currentSlug,  // Sprint 1070.C4 — optional. When provided, highlight the
                // pill matching this slug instead of index 0. Lets callers
                // preserve natural stage order (Build → Ship → Optimize)
                // and still highlight the current stage at its position.
}) {
  const [openDrawerSlug, setOpenDrawerSlug] = useState(null);
  const countRefs = useRef({});
  const safeAccent = accent || "#3B82F6";

  return (
    <div
      className="w-full mb-4"
      data-testid={`${studioId}-mission-breadcrumb`}
      data-mission={`${studioId}-loop`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)]">
          {t ? t("Mission") : "Mission"}:
        </span>
        <span
          className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: safeAccent }}
          data-testid={`${studioId}-mission-breadcrumb-label`}
        >
          {missionLabel}
        </span>
      </div>
      <div
        className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin"
        data-testid={`${studioId}-mission-breadcrumb-stages`}
      >
        {sections.map((s, i) => {
          // Sprint 1070.C4 — when `currentSlug` is provided, highlight that
          // pill regardless of its index (natural-order pattern). Falls
          // back to legacy "first pill is current" behavior otherwise.
          const isFirst = currentSlug ? s.slug === currentSlug : i === 0;
          const hasCount = typeof s.count === "number" && s.count > 0;
          const pillStyle = isFirst
            ? { borderColor: `${safeAccent}55`, backgroundColor: `${safeAccent}15`, color: safeAccent }
            : {
                borderColor: "var(--app-border)",
                backgroundColor: "var(--app-surface)",
                color: "var(--app-text)",
                opacity: 0.85,
              };
          // Pill body (either Link or div depending on stage having an href)
          const pillBody = (
            <>
              <span
                className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px] font-bold shrink-0"
                style={{
                  backgroundColor: isFirst ? `${safeAccent}30` : "var(--app-border)",
                  color: isFirst ? safeAccent : "var(--app-text-muted)",
                }}
              >
                {i + 1}
              </span>
              {s.label}
              {hasCount && renderDrawer && (
                <button
                  ref={(el) => { countRefs.current[s.slug] = el; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpenDrawerSlug(openDrawerSlug === s.slug ? null : s.slug);
                  }}
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: isFirst ? safeAccent : "var(--app-text-muted)",
                    color: "var(--app-bg)",
                  }}
                  title={t ? t("Click to see oldest at this stage") : "Oldest at stage"}
                  data-testid={`${studioId}-mission-breadcrumb-count-${s.slug}`}
                  data-count={s.count}
                >
                  {s.count}
                </button>
              )}
              {hasCount && !renderDrawer && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold"
                  style={{
                    backgroundColor: isFirst ? safeAccent : "var(--app-text-muted)",
                    color: "var(--app-bg)",
                  }}
                  data-testid={`${studioId}-mission-breadcrumb-count-${s.slug}`}
                  data-count={s.count}
                >
                  {s.count}
                </span>
              )}
            </>
          );

          // Whole pill is clickable when href is provided (Creative pattern).
          const pillClasses = "flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium border transition-all hover:opacity-100";

          return (
            <div key={s.slug} className="flex items-center shrink-0">
              {s.href ? (
                <Link
                  to={s.href}
                  className={pillClasses}
                  style={pillStyle}
                  data-testid={`${studioId}-mission-breadcrumb-stage-${s.slug}`}
                  data-current={isFirst}
                  data-count={hasCount ? s.count : undefined}
                >
                  {pillBody}
                </Link>
              ) : (
                <div
                  className={pillClasses}
                  style={pillStyle}
                  data-testid={`${studioId}-mission-breadcrumb-stage-${s.slug}`}
                  data-current={isFirst}
                  data-count={hasCount ? s.count : undefined}
                >
                  {pillBody}
                </div>
              )}
              {openDrawerSlug === s.slug && renderDrawer && renderDrawer({
                slug: s.slug,
                count: s.count,
                section: s,
                anchorRef: { current: countRefs.current[s.slug] },
                onClose: () => setOpenDrawerSlug(null),
              })}
              {i < sections.length - 1 && (
                <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
